import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { AxiosError } from 'axios'

import { axiosInstance } from '../helpers'
import {
    CreatePremiumInput,
    Premium,
    UpdatePremiumInput,
} from '../models/Concession'
import { useUser } from './useUser'

const PREMIUMS_KEY = 'premiums'
const BASE = '/beta/api/concession/premiums'

const extractError = (error: unknown, fallback: string): string => {
    const data = (error as AxiosError)?.response?.data as
        | Record<string, unknown>
        | undefined

    if (data) {
        const nonField = data.non_field_errors
        if (Array.isArray(nonField) && nonField.length > 0) {
            return String(nonField[0])
        }
        const firstKey = Object.keys(data)[0]
        if (firstKey) {
            const val = data[firstKey]
            if (Array.isArray(val) && val.length > 0) {
                return `${firstKey}: ${val[0]}`
            }
            if (typeof val === 'string') {
                return val
            }
        }
    }

    return fallback
}

export const usePremiums = () => {
    const queryClient = useQueryClient()
    const { workspaceUser } = useUser()

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data: premiums, isLoading, error, refetch } = useQuery<
        Premium[],
        Error
    >({
        queryKey: [PREMIUMS_KEY, apartmentId],
        queryFn: async () => {
            const res = await axiosInstance.get<Premium[]>(`${BASE}/`)
            return res.data
        },
        enabled: isApartment && !!apartmentId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: [PREMIUMS_KEY] })

    const createPremium = useMutation({
        mutationFn: async (body: CreatePremiumInput) => {
            const res = await axiosInstance.post<Premium>(`${BASE}/`, body)
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Premium created')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to create premium'))
        },
    })

    const updatePremium = useMutation({
        mutationFn: async ({
            id,
            body,
        }: {
            id: number
            body: UpdatePremiumInput
        }) => {
            const res = await axiosInstance.patch<Premium>(
                `${BASE}/${id}/`,
                body,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Premium updated')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to update premium'))
        },
    })

    const toggleActive = useMutation({
        mutationFn: async ({
            id,
            isActive,
        }: {
            id: number
            isActive: boolean
        }) => {
            const res = await axiosInstance.patch<Premium>(`${BASE}/${id}/`, {
                is_active: isActive,
            })
            return res.data
        },
        onSuccess: () => {
            invalidate()
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to update premium'))
        },
    })

    const deletePremium = useMutation({
        mutationFn: async (id: number) => {
            await axiosInstance.delete(`${BASE}/${id}/`)
        },
        onSuccess: () => {
            invalidate()
            toast.success('Premium deleted')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to delete premium'))
        },
    })

    return {
        premiums: premiums ?? [],
        isLoading,
        error,
        refetch,
        isApartment,
        createPremium,
        updatePremium,
        toggleActive,
        deletePremium,
    }
}
